/*
 * ForgeOS Fulfilment System
 * Version: 0.2.0
 *
 * Controls live product-page fulfilment messaging based on:
 * - Current Shopify inventory
 * - Continue-selling policy
 * - Customer-selected quantity
 */

(() => {
  'use strict';

  /**
   * Safely converts a value into an integer.
   */
  function forgeParseNumber(value, fallback = 0) {
    const parsedValue = Number.parseInt(value, 10);

    return Number.isNaN(parsedValue) ? fallback : parsedValue;
  }

  /**
   * Finds the product area associated with a fulfilment panel.
   *
   * Horizon may place the quantity selector outside the exact product form,
   * so we search the nearest Shopify section rather than only the form.
   */
  function forgeGetProductScope(container) {
    return (
      container.closest('.shopify-section') ||
      container.closest('[id^="shopify-section-"]') ||
      container.closest('section') ||
      document
    );
  }

  /**
   * Finds the quantity input belonging to the current product.
   */
  function forgeGetQuantityInput(container) {
    const scope = forgeGetProductScope(container);

    return (
      scope.querySelector('input[name="quantity"]') ||
      scope.querySelector('input[name="quantity"][type="number"]') ||
      scope.querySelector('quantity-selector input') ||
      scope.querySelector('.quantity-selector input') ||
      scope.querySelector('[data-quantity-input]')
    );
  }

  /**
   * Determines and displays the appropriate fulfilment state.
   */
  function forgeUpdateFulfilment(container) {
    if (!(container instanceof HTMLElement)) {
      return;
    }

    const inventory = forgeParseNumber(container.dataset.inventory, 0);
    const tracked = container.dataset.tracked === 'true';
    const continueSelling = container.dataset.continue === 'true';

    const quantityInput = forgeGetQuantityInput(container);
    const requestedQuantity = Math.max(
      forgeParseNumber(quantityInput?.value, 1),
      1
    );

    let fulfilmentState = 'ready';

    if (tracked && continueSelling && inventory <= 0) {
      fulfilmentState = 'order';
    } else if (
      tracked &&
      continueSelling &&
      inventory > 0 &&
      requestedQuantity > inventory
    ) {
      fulfilmentState = 'mixed';
    }

    container.querySelectorAll('[data-forge-state]').forEach((panel) => {
      panel.hidden = panel.dataset.forgeState !== fulfilmentState;
    });

    const readyCount = container.querySelector(
      '[data-forge-ready-count]'
    );

    const orderCount = container.querySelector(
      '[data-forge-order-count]'
    );

    if (readyCount) {
      readyCount.textContent = String(
        Math.min(Math.max(inventory, 0), requestedQuantity)
      );
    }

    if (orderCount) {
      orderCount.textContent = String(
        Math.max(requestedQuantity - Math.max(inventory, 0), 0)
      );
    }

    container.dataset.activeForgeState = fulfilmentState;
  }

  /**
   * Updates every Forge fulfilment panel currently on the page.
   */
  function forgeUpdateAllFulfilmentPanels() {
    document
      .querySelectorAll('[data-forge-fulfilment]')
      .forEach(forgeUpdateFulfilment);
  }

  /**
   * Checks whether an interaction came from a quantity control.
   */
  function forgeIsQuantityControl(element) {
    if (!(element instanceof Element)) {
      return false;
    }

    return Boolean(
      element.matches('input[name="quantity"]') ||
      element.closest('quantity-selector') ||
      element.closest('.quantity-selector') ||
      element.closest('[data-quantity-selector]') ||
      element.closest('[data-quantity-input]')
    );
  }

  /**
   * Listen for direct changes to quantity inputs.
   */
  document.addEventListener('input', (event) => {
    if (forgeIsQuantityControl(event.target)) {
      forgeUpdateAllFulfilmentPanels();
    }
  });

  document.addEventListener('change', (event) => {
    if (forgeIsQuantityControl(event.target)) {
      forgeUpdateAllFulfilmentPanels();
    }
  });

  /**
   * Quantity plus/minus controls often update the input after their click
   * handler runs, so update ForgeOS on the next animation frame.
   */
  document.addEventListener('click', (event) => {
    if (!forgeIsQuantityControl(event.target)) {
      return;
    }

    window.requestAnimationFrame(() => {
      forgeUpdateAllFulfilmentPanels();

      window.setTimeout(forgeUpdateAllFulfilmentPanels, 100);
    });
  });

  /**
   * Reinitialise when Shopify reloads a section in the theme editor.
   */
  document.addEventListener('shopify:section:load', () => {
    forgeUpdateAllFulfilmentPanels();
  });

  /**
   * Horizon can replace product markup when a variant changes.
   * This observer detects newly rendered Forge panels.
   */
  const forgeObserver = new MutationObserver((mutations) => {
    const forgeMarkupChanged = mutations.some((mutation) => {
      return Array.from(mutation.addedNodes).some((node) => {
        return (
          node instanceof Element &&
          (
            node.matches('[data-forge-fulfilment]') ||
            node.querySelector('[data-forge-fulfilment]')
          )
        );
      });
    });

    if (forgeMarkupChanged) {
      forgeUpdateAllFulfilmentPanels();
    }
  });

  function forgeInitialise() {
    forgeUpdateAllFulfilmentPanels();

    forgeObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', forgeInitialise, {
      once: true
    });
  } else {
    forgeInitialise();
  }
})();